import * as THREE from 'three';
import type RAPIER from '@dimforge/rapier3d-compat';
import type { Physics } from '../physics/Physics';
import type { LevelData, CollectibleTier, CollectiblePlacement } from '../levels/types';
import { TrackSpline, type Frame } from './Spline';
import { getSurface, type SurfaceId, type SurfaceMaterial } from './Surfaces';
import { clamp, smoothstep } from '../util/math';

// Cross-section beyond the playable [-1, 1] band rises into grassy bamboo hills
// that keep the ball on the track; only the authored open edges drop away.
const BANK_OUTER = 2.4; // lateralNorm the side hills extend to
const CLIFF_DROP = 38; // how far an open edge falls away
const FLANK_OUTER = 9; // lateralNorm the (visual) mountainside extends to
// The chasm cuts only the central band; a solid floor margin remains between
// the cut and the side walls, so drifting to the very edge lands on a ledge
// instead of falling in (the bounce-pad row still launches centered lines).
const GAP_HALF_NORM = 0.9;

export interface CollectibleInstance {
  pos: THREE.Vector3;
  tier: CollectibleTier;
  radius: number;
  collected: boolean;
  meshIndex: number;
  basePos: THREE.Vector3;
  phase: number;
}

export interface BouncePad {
  pos: THREE.Vector3;
  radius: number;
}

export interface BambooStalk {
  pos: THREE.Vector3;
  radius: number;
  snapped: boolean;
  meshIndex: number;
  matrix: THREE.Matrix4; // original (standing) transform
  falling: boolean; // currently animating its topple
  fallTime: number;
  pivot: THREE.Vector3; // base point it rotates around as it falls
  axis: THREE.Vector3; // horizontal axis it topples about
}

interface Checkpoint {
  t: number;
  pos: THREE.Vector3;
  frame: Frame;
}

const PICKUP_RADIUS: Record<CollectibleTier, number> = {
  fruit: 1.1,
  food: 1.2,
  coin: 1.3,
  envelope: 1.6,
};

/**
 * Generic level runner (§5/§10). Consumes LevelData and produces the visual
 * track + Rapier colliders + collectibles/obstacles/checkpoints. Adding a course
 * is data-only.
 */
export class World {
  readonly group = new THREE.Group();
  readonly spline: TrackSpline;
  readonly collectibles: CollectibleInstance[] = [];
  readonly bouncePads: BouncePad[] = [];
  readonly bambooStalks: BambooStalk[] = [];
  readonly obstacleHandles = new Set<number>();
  readonly bouncePadRadius = 2.2;
  private bambooMesh?: THREE.InstancedMesh;
  /** Spline arc-length t of the finish line (run-out plateau lies beyond it). */
  readonly finishT: number;

  private checkpoints: Checkpoint[] = [];
  private collectMeshes = new Map<CollectibleTier, THREE.InstancedMesh>();
  private rampDirty: { t: number; lateral: number; length: number; rise: number; width: number }[];
  private dummy = new THREE.Object3D();

  constructor(
    private level: LevelData,
    private physics: Physics,
  ) {
    this.spline = new TrackSpline(level.controlPoints, level.halfWidths, level.banks);
    this.rampDirty = level.ramps;
    // The finish line sits at a control point; everything beyond it is a flat
    // run-out. Content is authored in "playable" space (0..1 = start..finish);
    // toSplineT maps that onto the arc-length spline so the run-out doesn't
    // shift placements.
    const fp = level.controlPoints[level.finishControlIndex];
    this.finishT = this.spline.project(new THREE.Vector3(fp.x, fp.y, fp.z)).t;
    this.buildTrack();
    this.buildFlanks();
    this.buildEdgeWalls();
    this.buildCheckpoints();
    this.buildObstacles();
    this.buildBouncePads();
    this.buildCollectibles();
    this.buildScenery();
    this.buildGaps();
    this.buildStartApron();
    this.buildStartFinish();
  }

  /** Playable t (0..1, start..finish) -> spline arc-length t. */
  private toSplineT(playT: number): number {
    return playT * this.finishT;
  }

  /** Spline arc-length t -> playable t. */
  private toPlayT(splineT: number): number {
    return this.finishT > 0 ? splineT / this.finishT : splineT;
  }

  /** Height the surface is raised at (spline t, lateralNorm) by any ramp. */
  private rampHeight(t: number, lateralNorm: number): number {
    const pt = this.toPlayT(t);
    let h = 0;
    for (const r of this.rampDirty) {
      const half = r.length / 2;
      if (pt < r.t - half || pt > r.t + half) continue;
      const along = (pt - (r.t - half)) / r.length; // 0..1
      const latDist = Math.abs(lateralNorm - r.lateral);
      if (latDist > r.width) continue;
      const latFall = 1 - smoothstep(latDist / r.width);
      // Rise toward the lip, then a short steep drop creates the takeoff edge.
      let profile: number;
      if (along < 0.8) profile = smoothstep(along / 0.8);
      else profile = 1 - (along - 0.8) / 0.2;
      h = Math.max(h, r.rise * Math.max(0, profile) * latFall);
    }
    return h;
  }

  private isOpenEdge(t: number, side: 'left' | 'right'): boolean {
    const pt = this.toPlayT(t);
    for (const e of this.level.openEdges) {
      if (pt < e.tStart || pt > e.tEnd) continue;
      if (e.side === 'both' || e.side === side) return true;
    }
    return false;
  }

  /** True when (spline t) is over a chasm in the floor. */
  isOverGap(t: number): boolean {
    const pt = this.toPlayT(t);
    for (const g of this.level.gaps) {
      if (pt >= g.tStart && pt <= g.tEnd) return true;
    }
    return false;
  }

  /** Height of the side terrain at (t, lateralNorm) for |lateralNorm| > 1. */
  private bankHeight(t: number, lateralNorm: number): number {
    const a = Math.abs(lateralNorm);
    if (a <= 1) return 0;
    const f = clamp((a - 1) / (BANK_OUTER - 1), 0, 1);
    const side: 'left' | 'right' = lateralNorm < 0 ? 'left' : 'right';
    if (this.isOpenEdge(t, side)) return -CLIFF_DROP * f * f; // open cliff
    return this.level.bankHeight * f * f; // grassy hill
  }

  /** Tall central hill in "fork" sections — the path splits left/right around it. */
  private moundHeight(t: number, lateralNorm: number): number {
    const mounds = this.level.mounds;
    if (!mounds || mounds.length === 0) return 0;
    const a = Math.abs(lateralNorm);
    if (a > 0.5) return 0;
    const pt = this.toPlayT(t);
    let h = 0;
    for (const m of mounds) {
      if (pt < m.tStart || pt > m.tEnd) continue;
      const fadeT = Math.min(1, Math.min(pt - m.tStart, m.tEnd - pt) / 0.05);
      const latF = 0.5 + 0.5 * Math.cos((a / 0.5) * Math.PI); // 1 at centre, 0 by 0.5
      h = Math.max(h, 12 * Math.max(0, fadeT) * latF);
    }
    return h;
  }

  /** A raised "high lane" (the fast line) on one side, split into a few high
   * segments by ground-level "fork" gaps. Each segment ramps UP gently at its
   * entry (so getting on is smooth) and ends in a short, steep launch ledge that
   * drops back to ground (so you sail off it) — and the ledges grow taller
   * toward the finish for a bigger send. The inner edge toward centre is always
   * a steep step, so a slip toward the middle drops you onto the low lane until
   * the next fork gap lets you climb back on. */
  private shelfHeight(t: number, lateralNorm: number): number {
    const s = this.level.shelf;
    if (!s) return 0;
    if (Math.sign(lateralNorm) !== s.side) return 0;
    const pt = this.toPlayT(t);
    const endT = s.tEnd ?? 0.86;
    if (pt < s.tStart || pt > endT) return 0;
    // Inside a fork gap the lanes merge at ground level.
    for (const f of s.forks) if (pt >= f.tStart && pt <= f.tEnd) return 0;

    // Lateral profile: flat (low) until innerLN, then a steep step up.
    const a = Math.abs(lateralNorm);
    const edge = 0.2;
    let latF: number;
    if (a <= s.innerLN) latF = 0;
    else if (a >= s.innerLN + edge) latF = 1;
    else latF = smoothstep((a - s.innerLN) / edge);
    if (latF === 0) return 0;

    // Find the high segment [segStart, segEnd] that pt sits in, and its index.
    let segStart = s.tStart;
    let segEnd = endT;
    let segIndex = 0;
    for (const f of s.forks) {
      if (f.tEnd < pt) {
        segStart = Math.max(segStart, f.tEnd);
        segIndex++;
      } else if (f.tStart > pt) {
        segEnd = Math.min(segEnd, f.tStart);
        break;
      }
    }
    const segs = s.forks.length + 1;
    // Gentle climb in, then a short steep launch ledge at the end.
    const rise = smoothstep(Math.min(1, (pt - segStart) / 0.08));
    const dropLen = 0.02;
    const drop = pt > segEnd - dropLen ? smoothstep((segEnd - pt) / dropLen) : 1;
    const env = Math.min(rise, Math.max(0, drop));
    // Ledges grow taller toward the finish for a bigger send off the end.
    const h = s.height * (0.7 + (0.7 * segIndex) / Math.max(1, segs - 1));
    return h * latF * env;
  }

  /** Total surface height at (t, lateralNorm): ramps + central mounds on the
   * playable band, hills/cliffs beyond, plus the high-lane shelf on its side. */
  private surfaceHeight(t: number, lateralNorm: number): number {
    const base =
      Math.abs(lateralNorm) <= 1
        ? this.rampHeight(t, lateralNorm) + this.moundHeight(t, lateralNorm)
        : this.bankHeight(t, lateralNorm);
    return base + this.shelfHeight(t, lateralNorm);
  }

  getSurfaceAt(t: number, lateralNorm: number): SurfaceMaterial {
    const pt = this.toPlayT(t);
    let id: SurfaceId = 'grass';
    for (const z of this.level.surfaceZones) {
      if (pt < z.tStart || pt > z.tEnd) continue;
      const lo = z.lateralMin ?? -1;
      const hi = z.lateralMax ?? 1;
      if (lateralNorm < lo || lateralNorm > hi) continue;
      id = z.surface;
    }
    return getSurface(id);
  }

  private buildTrack(): void {
    const S = 480;
    const C = 20; // columns span -BANK_OUTER..BANK_OUTER (playable is |l|<=1)
    const positions: number[] = [];
    const colors: number[] = [];
    const indices: number[] = [];

    const grass = new THREE.Color(0x5fae54);
    const hillTop = new THREE.Color(0x3c7a3a);
    const cliff = new THREE.Color(0x6b5d4a);
    // On snow courses the side hills bordering the track read as snow-covered
    // banks (frosted near the edge, full snow up top) instead of green. The
    // playable surface colours (grass/ice/mud) are left alone so the racing
    // line stays legible.
    const snowy = this.level.theme === 'snow';
    const bankLow = snowy ? new THREE.Color(0xc4d6cb) : grass;
    const bankHigh = snowy ? new THREE.Color(0xeef3f7) : hillTop;

    for (let s = 0; s < S; s++) {
      const t = s / (S - 1);
      const fr = this.spline.frameAt(t);
      for (let c = 0; c < C; c++) {
        const lateralNorm = -BANK_OUTER + (2 * BANK_OUTER * c) / (C - 1);
        const playable = Math.abs(lateralNorm) <= 1;
        const h = this.surfaceHeight(t, lateralNorm);
        const v = fr.pos
          .clone()
          .addScaledVector(fr.right, lateralNorm * fr.halfWidth)
          .addScaledVector(fr.up, h);
        positions.push(v.x, v.y, v.z);

        let col: THREE.Color;
        if (playable) {
          col = new THREE.Color(this.getSurfaceAt(t, lateralNorm).color);
          // Subtle stripe so motion/speed reads on plain surfaces.
          const stripe = s % 8 < 4 ? 1.0 : 0.93;
          col.multiplyScalar(stripe);
        } else {
          const side: 'left' | 'right' = lateralNorm < 0 ? 'left' : 'right';
          const f = clamp((Math.abs(lateralNorm) - 1) / (BANK_OUTER - 1), 0, 1);
          col = this.isOpenEdge(t, side) ? cliff.clone() : bankLow.clone().lerp(bankHigh, f);
        }
        colors.push(col.r, col.g, col.b);
      }
    }

    for (let s = 0; s < S - 1; s++) {
      // Over a chasm, cut only the central band so the side hills bridge across
      // (keeps the valley visually continuous; the chasm is a slot in the floor).
      const tMid = (s + 0.5) / (S - 1);
      const overGap = this.isOverGap(tMid);
      for (let c = 0; c < C - 1; c++) {
        if (overGap) {
          const lnc = -BANK_OUTER + (2 * BANK_OUTER * c) / (C - 1);
          const lnc1 = -BANK_OUTER + (2 * BANK_OUTER * (c + 1)) / (C - 1);
          if (Math.abs((lnc + lnc1) / 2) < GAP_HALF_NORM) continue;
        }
        const a = s * C + c;
        const b = a + 1;
        const d = a + C;
        const e = d + 1;
        indices.push(a, d, b, b, d, e);
      }
    }

    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geom.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    geom.setIndex(indices);
    geom.computeVertexNormals();

    const mat = new THREE.MeshStandardMaterial({
      vertexColors: true,
      roughness: 0.95,
      metalness: 0.0,
      side: THREE.DoubleSide,
    });
    const mesh = new THREE.Mesh(geom, mat);
    mesh.receiveShadow = true;
    this.group.add(mesh);

    // Rapier trimesh collider for the track surface.
    const verts = new Float32Array(positions);
    const idx = new Uint32Array(indices);
    const body = this.physics.world.createRigidBody(
      this.physics.RAPIER.RigidBodyDesc.fixed(),
    );
    const desc = this.physics.RAPIER.ColliderDesc.trimesh(verts, idx)
      .setFriction(1.0)
      .setRestitution(0.0);
    this.physics.world.createCollider(desc, body);
  }

  /**
   * Visual-only mountainsides extending from the side hills outward and down
   * into the valley, so the track reads as carved into a mountain instead of
   * floating in space. Shares its inner edge with the track ribbon's outer edge
   * for a seamless join. No collider (the track ribbon already blocks falling).
   */
  private buildFlanks(): void {
    // Match the track ribbon's longitudinal resolution so the shared inner edge
    // tessellates identically and the join stays watertight (a coarser flank
    // chorded across the ribbon's curve, opening seams on tight/steep sections).
    const S = 480;
    const FC = 8; // columns per side, from BANK_OUTER out to FLANK_OUTER
    const grass = new THREE.Color(0x4f9a48);
    const rock = new THREE.Color(0x6f6256);
    const snow = new THREE.Color(0xeef2f6);
    const snowy = this.level.theme === 'snow';

    const positions: number[] = [];
    const colors: number[] = [];
    const indices: number[] = [];
    let base = 0;

    for (const sideSign of [-1, 1]) {
      const startIndex = base;
      for (let s = 0; s < S; s++) {
        const t = s / (S - 1);
        const fr = this.spline.frameAt(t);
        // Match the ribbon's FULL outer-edge height (banks + any high-lane shelf),
        // so the inner flank column lands exactly on the track's outer edge.
        const innerH = this.surfaceHeight(t, sideSign * BANK_OUTER);
        for (let c = 0; c < FC; c++) {
          const f = c / (FC - 1); // 0 at the hill edge, 1 at the far flank
          const ln = BANK_OUTER + (FLANK_OUTER - BANK_OUTER) * f;
          const d = ln - BANK_OUTER;
          // Descend steeply from the hill edge, with rolling ridges + a few peaks.
          const rolling =
            7 * Math.sin(t * Math.PI * 9 + sideSign * 1.3) +
            5 * Math.sin(d * 0.5 + t * 20) +
            6 * Math.cos(t * Math.PI * 4 + sideSign);
          let h = innerH - d * 9 + rolling * Math.min(1, f * 1.5);
          // A distant rim rises back up to read as far peaks.
          if (f > 0.7) h += (f - 0.7) * 70;
          // Offset along the BANKED frame (right + up) exactly like the track
          // ribbon's outer column, not by absolute Y — otherwise on banked or
          // steep sections the inner edge drifts off the ribbon and tears a seam.
          const p = fr.pos
            .clone()
            .addScaledVector(fr.right, sideSign * ln * fr.halfWidth)
            .addScaledVector(fr.up, h);
          positions.push(p.x, p.y, p.z);

          // Colour by relative height. Green courses: green low, rock mid, snow
          // on the peaks. Snow courses: snow blankets the slopes, with bare rock
          // showing through only on the lower, steeper faces toward the valley.
          const rel = h - innerH;
          let col: THREE.Color;
          if (snowy) col = rock.clone().lerp(snow, clamp((rel + 22) / 34, 0, 1));
          else if (rel > 18) col = rock.clone().lerp(snow, Math.min(1, (rel - 18) / 30));
          else if (rel > -6) col = grass.clone().lerp(rock, Math.min(1, (rel + 6) / 24));
          else col = grass.clone();
          colors.push(col.r, col.g, col.b);
        }
      }
      for (let s = 0; s < S - 1; s++) {
        for (let c = 0; c < FC - 1; c++) {
          const a = startIndex + s * FC + c;
          const b = a + 1;
          const dd = a + FC;
          const e = dd + 1;
          // Wind so normals face up regardless of side.
          if (sideSign < 0) indices.push(a, dd, b, b, dd, e);
          else indices.push(a, b, dd, b, e, dd);
        }
      }
      base += S * FC;
    }

    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geom.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    geom.setIndex(indices);
    geom.computeVertexNormals();
    const mat = new THREE.MeshStandardMaterial({
      vertexColors: true,
      roughness: 1,
      metalness: 0,
      side: THREE.DoubleSide,
      flatShading: true,
    });
    const mesh = new THREE.Mesh(geom, mat);
    mesh.receiveShadow = true;
    this.group.add(mesh);
  }

  /**
   * Invisible containment walls just outside the playable edge so the panda
   * can't slip through the side hills (kid-friendly). Skipped at the open cliffs
   * and the chasm, where falling is intended. Collider-only — the grassy hills
   * provide the visuals.
   */
  private buildEdgeWalls(): void {
    const S = 300;
    const WALL_LN = 1.1; // just outside the playable band
    const WALL_H = 6; // height above the surface
    const positions: number[] = [];
    const indices: number[] = [];
    let base = 0;

    for (const sideSign of [-1, 1]) {
      const side: 'left' | 'right' = sideSign < 0 ? 'left' : 'right';
      const startIndex = base;
      for (let s = 0; s < S; s++) {
        const t = s / (S - 1);
        const fr = this.spline.frameAt(t);
        const ln = sideSign * WALL_LN;
        const bottom = fr.pos
          .clone()
          .addScaledVector(fr.right, ln * fr.halfWidth)
          .addScaledVector(fr.up, this.surfaceHeight(t, ln) - 0.5);
        const top = bottom.clone().addScaledVector(fr.up, WALL_H);
        positions.push(bottom.x, bottom.y, bottom.z, top.x, top.y, top.z);
      }
      for (let s = 0; s < S - 1; s++) {
        const t = (s + 0.5) / (S - 1);
        // Skip only at the open cliffs (intentional falls). Keep the wall across
        // the chasm so a sideways bounce can't escape past it onto the hill.
        if (this.isOpenEdge(t, side)) continue;
        const a = startIndex + s * 2; // bottom s
        const b = a + 1; // top s
        const c = a + 2; // bottom s+1
        const d = a + 3; // top s+1
        indices.push(a, c, b, b, c, d);
      }
      base += S * 2;
    }

    if (indices.length === 0) return;
    const verts = new Float32Array(positions);
    const idx = new Uint32Array(indices);
    const body = this.physics.world.createRigidBody(this.physics.RAPIER.RigidBodyDesc.fixed());
    const desc = this.physics.RAPIER.ColliderDesc.trimesh(verts, idx)
      .setFriction(0.4)
      .setRestitution(0.1);
    this.physics.world.createCollider(desc, body);
  }

  private buildCheckpoints(): void {
    const n = this.level.checkpointCount;
    for (let i = 0; i <= n; i++) {
      const t = this.toSplineT(i / n); // spline-space, evenly over the playable course
      const frame = this.spline.frameAt(t);
      const pos = frame.pos.clone().addScaledVector(frame.up, 1.0);
      this.checkpoints.push({ t, pos, frame });
    }
  }

  /**
   * True when a centered drop at (spline t) lands on solid, recoverable track:
   * not over a chasm, and not on a narrow drops-on-both-sides bridge — on those
   * a respawn tends to slide straight off again (no control during the respawn
   * window), which is exactly what caused the infinite-fall loop.
   */
  isSafeRespawnT(t: number): boolean {
    if (this.isOverGap(t)) return false;
    const pt = this.toPlayT(t);
    const PAD = 0.015;
    for (const e of this.level.openEdges) {
      if (e.side !== 'both') continue; // one-sided cliffs: a centered drop is fine
      if (pt >= e.tStart - PAD && pt <= e.tEnd + PAD) return false;
    }
    return true;
  }

  /**
   * Where to drop the panda after a fall (§5). Always on the centerline (the
   * middle of the track, so you don't immediately fall off again). If the fall
   * happened at a chasm, respawn on the far side; otherwise at the last safe
   * spot, never earlier than the last checkpoint passed. `backoff` retreats this
   * by that many extra checkpoints — used when the panda falls repeatedly without
   * progressing, so we move further back up the track to firmer ground.
   */
  respawnFor(
    fallT: number,
    lastSafeT: number,
    backoff = 0,
  ): { pos: THREE.Vector3; frame: Frame } {
    // Chasm fall: respawn on the designed far-side exit (always safe).
    for (const g of this.level.gaps) {
      const gs = this.toSplineT(g.tStart);
      const ge = this.toSplineT(g.tEnd);
      if (fallT >= gs - 0.02 && fallT <= ge + 0.08) {
        const gt = clamp(this.toSplineT(g.exitT), 0, 1);
        const fr = this.spline.frameAt(gt);
        return { pos: fr.pos.clone().addScaledVector(fr.up, 1.6), frame: fr };
      }
    }

    // Latest checkpoint at/under the fall point (checkpoints are ascending in t).
    let cpIdx = 0;
    for (let i = 0; i < this.checkpoints.length; i++) {
      if (this.checkpoints[i].t <= fallT + 1e-4) cpIdx = i;
      else break;
    }
    // Repeated falls: retreat extra checkpoints up the centerline.
    cpIdx = Math.max(0, cpIdx - backoff);
    // Never come to rest on a chasm or a both-side bridge — walk back to a
    // checkpoint that's actually safe to drop onto (checkpoint 0 is the start).
    while (cpIdx > 0 && !this.isSafeRespawnT(this.checkpoints[cpIdx].t)) cpIdx--;
    const cpT = this.checkpoints[cpIdx].t;

    // Prefer the finer last-safe progress only when it's a genuine improvement
    // and itself safe, and only when we're not deliberately backing off.
    let t = cpT;
    if (
      backoff === 0 &&
      lastSafeT > cpT &&
      lastSafeT <= fallT + 1e-4 &&
      this.isSafeRespawnT(lastSafeT)
    ) {
      t = lastSafeT;
    }
    t = clamp(t, 0, 1);
    const frame = this.spline.frameAt(t);
    const pos = frame.pos.clone().addScaledVector(frame.up, 1.6);
    return { pos, frame };
  }

  startFrame(): Frame {
    return this.spline.frameAt(0);
  }

  finishPos(): THREE.Vector3 {
    const fr = this.spline.frameAt(this.finishT);
    return fr.pos.clone().addScaledVector(fr.up, 1);
  }

  private buildObstacles(): void {
    const rockMat = new THREE.MeshStandardMaterial({ color: 0x7c7c7c, roughness: 1, flatShading: true });
    const logMat = new THREE.MeshStandardMaterial({ color: 0x6b4a2b, roughness: 0.9 });
    const fenceMat = new THREE.MeshStandardMaterial({ color: 0xb98a4a, roughness: 0.8 });
    const RAPIER = this.physics.RAPIER;

    for (const o of this.level.obstacles) {
      const ot = this.toSplineT(o.t);
      const fr = this.spline.frameAt(ot);
      const base = fr.pos
        .clone()
        .addScaledVector(fr.right, o.lateral * fr.halfWidth)
        .addScaledVector(fr.up, this.surfaceHeight(ot, o.lateral));

      let mesh: THREE.Mesh;
      let colliderDesc: RAPIER.ColliderDesc;
      let pos = base.clone();
      let quat = new THREE.Quaternion();

      if (o.type === 'rock') {
        const r = 1.1;
        mesh = new THREE.Mesh(new THREE.IcosahedronGeometry(r, 0), rockMat);
        pos = base.clone().addScaledVector(fr.up, r * 0.6);
        colliderDesc = RAPIER.ColliderDesc.ball(r);
      } else if (o.type === 'log') {
        const r = 0.7;
        const len = Math.min(fr.halfWidth * 1.3, 5);
        const geo = new THREE.CylinderGeometry(r, r, len, 14);
        mesh = new THREE.Mesh(geo, logMat);
        // Lay the cylinder across the track (local Y -> right).
        quat.setFromUnitVectors(new THREE.Vector3(0, 1, 0), fr.right);
        pos = base.clone().addScaledVector(fr.up, r);
        colliderDesc = RAPIER.ColliderDesc.cylinder(len / 2, r).setRotation({
          x: quat.x,
          y: quat.y,
          z: quat.z,
          w: quat.w,
        });
      } else {
        // fence: thin wide box standing up, across the track.
        const w = Math.min(fr.halfWidth * 1.0, 4);
        const hgt = 1.4;
        const thick = 0.3;
        mesh = new THREE.Mesh(new THREE.BoxGeometry(w, hgt, thick), fenceMat);
        const m = new THREE.Matrix4().makeBasis(fr.right, fr.up, fr.tangent);
        quat.setFromRotationMatrix(m);
        pos = base.clone().addScaledVector(fr.up, hgt / 2);
        colliderDesc = RAPIER.ColliderDesc.cuboid(w / 2, hgt / 2, thick / 2).setRotation({
          x: quat.x,
          y: quat.y,
          z: quat.z,
          w: quat.w,
        });
      }

      mesh.position.copy(pos);
      mesh.quaternion.copy(quat);
      mesh.castShadow = true;
      this.group.add(mesh);

      const body = this.physics.world.createRigidBody(
        RAPIER.RigidBodyDesc.fixed().setTranslation(pos.x, pos.y, pos.z),
      );
      const collider = this.physics.world.createCollider(
        colliderDesc.setFriction(0.8).setRestitution(0.2).setActiveEvents(
          RAPIER.ActiveEvents.COLLISION_EVENTS,
        ),
        body,
      );
      this.obstacleHandles.add(collider.handle);
    }
  }

  private buildBouncePads(): void {
    const mat = new THREE.MeshStandardMaterial({
      color: 0xff5fa2,
      emissive: 0x551133,
      roughness: 0.5,
    });
    for (const b of this.level.bouncePads) {
      const bt = this.toSplineT(b.t);
      const fr = this.spline.frameAt(bt);
      const pos = fr.pos
        .clone()
        .addScaledVector(fr.right, b.lateral * fr.halfWidth)
        .addScaledVector(fr.up, this.surfaceHeight(bt, b.lateral) + 0.25);
      const mesh = new THREE.Mesh(new THREE.CylinderGeometry(this.bouncePadRadius, this.bouncePadRadius * 1.1, 0.5, 18), mat);
      const m = new THREE.Matrix4().makeBasis(fr.right, fr.up, fr.tangent);
      mesh.quaternion.setFromRotationMatrix(m);
      mesh.position.copy(pos);
      this.group.add(mesh);
      this.bouncePads.push({ pos: pos.clone(), radius: this.bouncePadRadius });
    }
  }

  private collectibleMaterial(tier: CollectibleTier): { geo: THREE.BufferGeometry; mat: THREE.Material } {
    switch (tier) {
      case 'fruit':
        return {
          geo: new THREE.SphereGeometry(0.45, 12, 10),
          mat: new THREE.MeshStandardMaterial({ color: 0xff6b3d, roughness: 0.5, emissive: 0x3a1500 }),
        };
      case 'food':
        return {
          geo: new THREE.SphereGeometry(0.5, 14, 8).scale(1, 0.7, 1.2),
          mat: new THREE.MeshStandardMaterial({ color: 0xf5e6c0, roughness: 0.6, emissive: 0x2a2410 }),
        };
      case 'coin':
        return {
          geo: new THREE.CylinderGeometry(0.55, 0.55, 0.12, 20),
          mat: new THREE.MeshStandardMaterial({ color: 0xffd23d, metalness: 0.8, roughness: 0.25, emissive: 0x4a3500 }),
        };
      case 'envelope':
        return {
          geo: new THREE.BoxGeometry(0.7, 1.0, 0.12),
          mat: new THREE.MeshStandardMaterial({ color: 0xe0322f, roughness: 0.4, emissive: 0x550f0e }),
        };
    }
  }

  private buildCollectibles(): void {
    // A reward coin sitting in the centre of the finish line — collect it by
    // aiming for the middle as you cross. Added to every level automatically.
    const finishCoin: CollectiblePlacement = { t: 0.995, lateral: 0, tier: 'coin', height: 1.0 };
    const placements: CollectiblePlacement[] = [...this.level.collectibles, finishCoin];

    const byTier = new Map<CollectibleTier, number>();
    for (const c of placements) byTier.set(c.tier, (byTier.get(c.tier) ?? 0) + 1);

    const counters = new Map<CollectibleTier, number>();
    for (const [tier, count] of byTier) {
      const { geo, mat } = this.collectibleMaterial(tier);
      const inst = new THREE.InstancedMesh(geo, mat, count);
      inst.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      inst.castShadow = true;
      inst.frustumCulled = false;
      this.collectMeshes.set(tier, inst);
      this.group.add(inst);
      counters.set(tier, 0);
    }

    for (const c of placements) {
      const ct = this.toSplineT(c.t);
      const fr = this.spline.frameAt(ct);
      const surfH = this.surfaceHeight(ct, c.lateral);
      const pos = fr.pos
        .clone()
        .addScaledVector(fr.right, c.lateral * fr.halfWidth)
        .addScaledVector(fr.up, surfH + 1.0 + (c.height ?? 0));
      const idx = counters.get(c.tier)!;
      counters.set(c.tier, idx + 1);
      // Initialize the instance matrix so it renders before the first update().
      const mesh = this.collectMeshes.get(c.tier)!;
      this.dummy.position.copy(pos);
      this.dummy.rotation.set(0, 0, 0);
      this.dummy.scale.setScalar(1);
      this.dummy.updateMatrix();
      mesh.setMatrixAt(idx, this.dummy.matrix);
      this.collectibles.push({
        pos: pos.clone(),
        basePos: pos.clone(),
        tier: c.tier,
        radius: PICKUP_RADIUS[c.tier],
        collected: false,
        meshIndex: idx,
        phase: Math.random() * Math.PI * 2,
      });
    }
  }

  collect(item: CollectibleInstance): void {
    item.collected = true;
    const mesh = this.collectMeshes.get(item.tier);
    if (!mesh) return;
    this.dummy.position.copy(item.pos);
    this.dummy.scale.setScalar(0);
    this.dummy.updateMatrix();
    mesh.setMatrixAt(item.meshIndex, this.dummy.matrix);
    mesh.instanceMatrix.needsUpdate = true;
  }

  resetCollectibles(): void {
    for (const c of this.collectibles) c.collected = false;
  }

  /** Snap a bamboo stalk: start it toppling over (in the travel direction). */
  snapBamboo(stalk: BambooStalk, dir?: THREE.Vector3): void {
    if (stalk.snapped) return;
    stalk.snapped = true;
    stalk.falling = true;
    stalk.fallTime = 0;
    const up = new THREE.Vector3(0, 1, 0);
    if (dir && dir.lengthSq() > 1e-4) {
      const d = dir.clone();
      d.y = 0;
      d.normalize();
      stalk.axis.copy(up.cross(d)).normalize(); // topple in the travel direction
    } else {
      stalk.axis.set(Math.random() - 0.5, 0, Math.random() - 0.5).normalize();
    }
    if (stalk.axis.lengthSq() < 1e-4) stalk.axis.set(1, 0, 0);
  }

  resetBamboo(): void {
    if (!this.bambooMesh) return;
    for (const s of this.bambooStalks) {
      s.snapped = false;
      s.falling = false;
      s.fallTime = 0;
      this.bambooMesh.setMatrixAt(s.meshIndex, s.matrix);
    }
    this.bambooMesh.instanceMatrix.needsUpdate = true;
  }

  private animateBamboo(dt: number): void {
    if (!this.bambooMesh) return;
    const FALL_DUR = 0.5;
    let dirty = false;
    const rot = new THREE.Matrix4();
    const toPivot = new THREE.Matrix4();
    const fromPivot = new THREE.Matrix4();
    const out = new THREE.Matrix4();
    for (const s of this.bambooStalks) {
      if (!s.falling) continue;
      s.fallTime += dt;
      const k = Math.min(1, s.fallTime / FALL_DUR);
      const eased = 1 - Math.pow(1 - k, 3); // ease-out
      const theta = eased * 1.5; // topple to ~flat
      rot.makeRotationAxis(s.axis, theta);
      toPivot.makeTranslation(-s.pivot.x, -s.pivot.y, -s.pivot.z);
      fromPivot.makeTranslation(s.pivot.x, s.pivot.y, s.pivot.z);
      out.multiplyMatrices(fromPivot, rot).multiply(toPivot).multiply(s.matrix);
      this.bambooMesh.setMatrixAt(s.meshIndex, out);
      dirty = true;
      if (k >= 1) s.falling = false; // settled flat, leave it lying
    }
    if (dirty) this.bambooMesh.instanceMatrix.needsUpdate = true;
  }

  private buildScenery(): void {
    const dummy = new THREE.Object3D();
    const snowy = this.level.theme === 'snow';

    // Bamboo forest growing up the grassy side hills (skipped at open cliffs so
    // the fall spots read as gaps). Instanced for perf (§10). Stalks near the
    // playable edge don't block — rolling into one snaps it off and slows the
    // panda slightly (kid-friendly), tracked as data, not colliders.
    // Reserve headroom in the pool for the mound-divider bamboo placed below.
    const forestBudget = 420;
    const bambooCount = forestBudget + (this.level.mounds?.length ?? 0) * 32;
    const bambooGeo = new THREE.CylinderGeometry(0.16, 0.22, 6, 6);
    const bambooMat = new THREE.MeshStandardMaterial({ color: 0x6fae3f, roughness: 0.9 });
    const bamboo = new THREE.InstancedMesh(bambooGeo, bambooMat, bambooCount);
    this.bambooMesh = bamboo;
    let bi = 0;
    for (let i = 0; i < forestBudget * 2 && bi < forestBudget; i++) {
      const t = Math.random();
      const side: 'left' | 'right' = Math.random() < 0.5 ? 'left' : 'right';
      if (this.isOpenEdge(t, side)) continue; // leave cliffs open
      // ~18% grow right at the track edge (inside the containment wall) so they
      // can be clipped & snapped; the rest decorate the hills behind the wall.
      const isEdge = Math.random() < 0.18 && !this.isOverGap(t);
      const fr = this.spline.frameAt(t);
      const lateralNorm = (side === 'left' ? -1 : 1) * (isEdge ? 0.88 + Math.random() * 0.16 : 1.18 + Math.random() * 1.1);
      const h = this.bankHeight(t, lateralNorm) + 2.4;
      const p = fr.pos
        .clone()
        .addScaledVector(fr.right, lateralNorm * fr.halfWidth)
        .addScaledVector(fr.up, h);
      const scale = isEdge ? 0.7 + Math.random() * 0.5 : 0.7 + Math.random() * 1.6;
      dummy.position.copy(p);
      dummy.rotation.set((Math.random() - 0.5) * 0.2, Math.random() * Math.PI, (Math.random() - 0.5) * 0.2);
      dummy.scale.setScalar(scale);
      dummy.updateMatrix();
      const meshIndex = bi++;
      bamboo.setMatrixAt(meshIndex, dummy.matrix);

      // Only the edge stalks are snappable (distance-checked like pickups).
      if (isEdge) {
        this.bambooStalks.push({
          pos: p.clone(),
          radius: 1.1,
          snapped: false,
          meshIndex,
          matrix: dummy.matrix.clone(),
          falling: false,
          fallTime: 0,
          // Pivot at the base of the stalk (geo is 6 tall -> half-height 3).
          pivot: p.clone().addScaledVector(fr.up, -3 * scale),
          axis: new THREE.Vector3(1, 0, 0),
        });
      }
    }
    // Bamboo lines the crest of each central-hill fork so the divider reads as
    // a wall of bamboo — but spaced out and set wide of the very centre, so a
    // precise run straight down the ridge threads between them (and only a
    // sloppy/crossing line mows them down, since each snap costs a little mood).
    for (const m of this.level.mounds ?? []) {
      const steps = 6;
      for (let i = 0; i <= steps && bi < bambooCount; i++) {
        const pt = m.tStart + (m.tEnd - m.tStart) * (i / steps);
        const t = this.toSplineT(pt);
        const fr = this.spline.frameAt(t);
        for (const ln of [-0.24, 0.24]) {
          if (bi >= bambooCount) break;
          const h = this.surfaceHeight(t, ln) + 2.4;
          const p = fr.pos
            .clone()
            .addScaledVector(fr.right, ln * fr.halfWidth)
            .addScaledVector(fr.up, h);
          const scale = 0.8 + Math.random() * 0.7;
          dummy.position.copy(p);
          dummy.rotation.set((Math.random() - 0.5) * 0.15, Math.random() * Math.PI, (Math.random() - 0.5) * 0.15);
          dummy.scale.setScalar(scale);
          dummy.updateMatrix();
          const meshIndex = bi++;
          bamboo.setMatrixAt(meshIndex, dummy.matrix);
          // Ridge bamboo snaps too — charging straight over a fork mows it down.
          this.bambooStalks.push({
            pos: p.clone(),
            radius: 1.1,
            snapped: false,
            meshIndex,
            matrix: dummy.matrix.clone(),
            falling: false,
            fallTime: 0,
            pivot: p.clone().addScaledVector(fr.up, -3 * scale),
            axis: new THREE.Vector3(1, 0, 0),
          });
        }
      }
    }

    bamboo.count = bi;
    bamboo.castShadow = true;
    this.group.add(bamboo);

    // A few darker conifers up top for a wooded mountain feel.
    const treeCount = 130;
    const treeGeo = new THREE.ConeGeometry(2.2, 7, 7);
    const treeMat = new THREE.MeshStandardMaterial({
      color: snowy ? 0xb8ccc2 : 0x2f6b3a, // snow-flocked evergreens on snow courses
      roughness: 1,
      flatShading: true,
    });
    const trees = new THREE.InstancedMesh(treeGeo, treeMat, treeCount);
    let ti = 0;
    for (let i = 0; i < treeCount * 2 && ti < treeCount; i++) {
      const t = Math.random();
      const side: 'left' | 'right' = Math.random() < 0.5 ? 'left' : 'right';
      if (this.isOpenEdge(t, side)) continue;
      const fr = this.spline.frameAt(t);
      const lateralNorm = (side === 'left' ? -1 : 1) * (2.0 + Math.random() * 0.8);
      const h = this.bankHeight(t, lateralNorm) + 3;
      const p = fr.pos
        .clone()
        .addScaledVector(fr.right, lateralNorm * fr.halfWidth)
        .addScaledVector(fr.up, h);
      dummy.position.copy(p);
      dummy.rotation.set(0, Math.random() * Math.PI, 0);
      dummy.scale.setScalar(0.8 + Math.random() * 1.4);
      dummy.updateMatrix();
      trees.setMatrixAt(ti++, dummy.matrix);
    }
    trees.count = ti;
    trees.castShadow = true;
    this.group.add(trees);

    // A broad, rolling green valley floor far below, so the course reads as
    // sitting in a landscape instead of floating in empty sky. Undulating +
    // vertex-coloured for depth; no collider (falls are handled by reset).
    const minY = Math.min(...this.level.controlPoints.map((p) => p.y));
    const midZ = this.level.controlPoints[Math.floor(this.level.controlPoints.length / 2)].z;
    const valGeo = new THREE.PlaneGeometry(7000, 7000, 80, 80);
    const vpos = valGeo.attributes.position as THREE.BufferAttribute;
    const low = snowy ? new THREE.Color(0xcbd6dd) : new THREE.Color(0x3f7a44);
    const high = snowy ? new THREE.Color(0xeef3f7) : new THREE.Color(0x66ab55);
    const vcol: number[] = [];
    for (let i = 0; i < vpos.count; i++) {
      const x = vpos.getX(i);
      const yy = vpos.getY(i); // plane local y -> world z after rotation
      // Rolling hills, taller toward the far edges to suggest distant ranges.
      const r = Math.hypot(x, yy);
      const h =
        34 * Math.sin(x * 0.0016) * Math.cos(yy * 0.0014) +
        18 * Math.sin(x * 0.004 + yy * 0.003) +
        (r > 1800 ? (r - 1800) * 0.09 : 0);
      vpos.setZ(i, h);
      const c = low.clone().lerp(high, clamp((h + 30) / 90, 0, 1));
      vcol.push(c.r, c.g, c.b);
    }
    valGeo.setAttribute('color', new THREE.Float32BufferAttribute(vcol, 3));
    valGeo.computeVertexNormals();
    const valley = new THREE.Mesh(
      valGeo,
      new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 1, flatShading: true }),
    );
    valley.rotation.x = -Math.PI / 2;
    valley.position.set(0, minY - 70, midZ);
    valley.receiveShadow = true;
    this.group.add(valley);
  }

  /** Simple dark walls + a flat black floor for each chasm slot. Kept narrow
   * (aligned to the floor cut) so the bounce-jump clears it as before. */
  private buildGaps(): void {
    const wallMat = new THREE.MeshStandardMaterial({
      color: 0x2b2b30,
      roughness: 1,
      flatShading: true,
      side: THREE.DoubleSide,
    });
    const floorMat = new THREE.MeshBasicMaterial({ color: 0x05060a, side: THREE.DoubleSide });
    const DEPTH = 14; // how deep the chasm cuts below the lip
    const W = GAP_HALF_NORM; // walls align with the cut floor edge

    for (const g of this.level.gaps) {
      const positions: number[] = [];
      const indices: number[] = [];
      const addQuad = (a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3, d: THREE.Vector3) => {
        const base = positions.length / 3;
        for (const v of [a, b, c, d]) positions.push(v.x, v.y, v.z);
        indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
      };

      // Sample the floor top profile across the slot at the near (A) and far (B)
      // edges so the wall tops meet the track floor.
      const cols = 8;
      const edgeTops = (t: number): THREE.Vector3[] => {
        const fr = this.spline.frameAt(t);
        const pts: THREE.Vector3[] = [];
        for (let i = 0; i <= cols; i++) {
          const ln = -W + (2 * W * i) / cols;
          pts.push(
            fr.pos
              .clone()
              .addScaledVector(fr.right, ln * fr.halfWidth)
              .addScaledVector(fr.up, this.surfaceHeight(t, ln)),
          );
        }
        return pts;
      };
      // A hair inside the cut so the curtain tucks just under the intact floor
      // (hides the wall's top edge) without poking out into a visible frame.
      const MARGIN = 0.003;
      const gs = this.toSplineT(g.tStart) - MARGIN;
      const ge = this.toSplineT(g.tEnd) + MARGIN;
      const topA = edgeTops(gs);
      const topB = edgeTops(ge);
      const drop = (p: THREE.Vector3) => p.clone().setY(p.y - DEPTH);

      // Near & far walls (vertical curtains under the floor edge profile).
      for (let i = 0; i < cols; i++) {
        addQuad(topA[i], topA[i + 1], drop(topA[i + 1]), drop(topA[i]));
        addQuad(topB[i + 1], topB[i], drop(topB[i]), drop(topB[i + 1]));
      }
      // Side walls connecting near to far at each slot edge.
      addQuad(topB[0], topA[0], drop(topA[0]), drop(topB[0]));
      addQuad(topA[cols], topB[cols], drop(topB[cols]), drop(topA[cols]));

      const geom = new THREE.BufferGeometry();
      geom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      geom.setIndex(indices);
      geom.computeVertexNormals();
      this.group.add(new THREE.Mesh(geom, wallMat));

      // Flat black floor at the bottom of the slot.
      const fpos: number[] = [];
      for (const v of [drop(topA[0]), drop(topA[cols]), drop(topB[cols]), drop(topB[0])]) {
        fpos.push(v.x, v.y, v.z);
      }
      const fgeom = new THREE.BufferGeometry();
      fgeom.setAttribute('position', new THREE.Float32BufferAttribute(fpos, 3));
      fgeom.setIndex([0, 1, 2, 0, 2, 3]);
      this.group.add(new THREE.Mesh(fgeom, floorMat));
    }
  }

  /**
   * A broad grassy apron behind the start line. Without it the track simply
   * begins in mid-air and the chase cam (which sits behind the ball) sees empty
   * sky under/around the start. The apron drapes down to the valley so the
   * course reads as growing out of the hillside. Visual only.
   */
  private buildStartApron(): void {
    const fr = this.spline.frameAt(0);
    const forward = fr.tangent.clone(); // points into the course (increasing t)
    forward.y = 0;
    if (forward.lengthSq() < 1e-4) forward.set(0, 0, -1);
    forward.normalize();
    const behind = forward.clone().negate(); // away from the course (camera side)
    const right = fr.right.clone();
    right.y = 0;
    right.normalize();

    const surfaceY = fr.pos.y;

    // Near edge follows the start cross-section EXACTLY (track + side hills), so
    // it joins the ribbon with no seam. The skirt then runs only BACKWARD (never
    // forward, where the descending track would poke through it) and drops away
    // to merge into the valley.
    const cols = 14;
    const near: THREE.Vector3[] = [];
    const far: THREE.Vector3[] = [];
    const farBase = fr.pos.clone().addScaledVector(behind, 220);
    for (let i = 0; i <= cols; i++) {
      const ln = -BANK_OUTER + (2 * BANK_OUTER * i) / cols;
      near.push(
        fr.pos
          .clone()
          .addScaledVector(right, ln * fr.halfWidth)
          // A hair ABOVE the track edge so the apron overlaps it (no thin dark
          // vertical face — that was the crack at the start line).
          .addScaledVector(fr.up, this.surfaceHeight(0, ln) + 0.06),
      );
      far.push(farBase.clone().addScaledVector(right, ln * 2.0 * fr.halfWidth).setY(surfaceY - 70));
    }

    const positions: number[] = [];
    const colors: number[] = [];
    const snowy = this.level.theme === 'snow';
    // Match the apron behind the start to the course theme so it doesn't read as
    // a green patch against snowy banks.
    const green = snowy ? new THREE.Color(0xdfe8ee) : new THREE.Color(0x5aa64f);
    const deep = snowy ? new THREE.Color(0xb9c8d2) : new THREE.Color(0x40793f);
    const push = (v: THREE.Vector3, c: THREE.Color) => {
      positions.push(v.x, v.y, v.z);
      colors.push(c.r, c.g, c.b);
    };
    const indices: number[] = [];
    for (let i = 0; i < cols; i++) {
      const b = positions.length / 3;
      push(near[i], green);
      push(near[i + 1], green);
      push(far[i + 1], deep);
      push(far[i], deep);
      indices.push(b, b + 1, b + 2, b, b + 2, b + 3);
    }
    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geom.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    geom.setIndex(indices);
    geom.computeVertexNormals();
    const mesh = new THREE.Mesh(
      geom,
      new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 1, side: THREE.DoubleSide }),
    );
    mesh.receiveShadow = true;
    this.group.add(mesh);
  }

  private gate(t: number, color: number, label: 'start' | 'finish'): void {
    const fr = this.spline.frameAt(t);
    const hw = fr.halfWidth;
    const postH = 6.5;
    const postOff = hw + 0.8;
    const postMat = new THREE.MeshStandardMaterial({ color, roughness: 0.6 });
    const bannerMat = new THREE.MeshStandardMaterial({
      color: label === 'finish' ? 0xffffff : color,
      roughness: 0.5,
      emissive: label === 'finish' ? 0x111111 : 0x000000,
    });

    // Build the arch in LOCAL space (x = right, y = up, z = along track), then
    // place/orient the whole group, so the parts are always connected.
    const arch = new THREE.Group();
    const add = (geo: THREE.BufferGeometry, mat: THREE.Material, x: number, y: number) => {
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(x, y, 0);
      mesh.castShadow = true;
      arch.add(mesh);
    };
    const postGeo = new THREE.BoxGeometry(0.6, postH, 0.6);
    add(postGeo, postMat, -postOff, postH / 2);
    add(postGeo, postMat, postOff, postH / 2);
    add(new THREE.BoxGeometry(postOff * 2 + 0.6, 0.6, 0.6), postMat, 0, postH); // crossbeam
    add(new THREE.BoxGeometry(postOff * 2 - 0.4, 1.6, 0.2), bannerMat, 0, postH - 1.1); // banner

    arch.position.copy(fr.pos);
    arch.quaternion.setFromRotationMatrix(new THREE.Matrix4().makeBasis(fr.right, fr.up, fr.tangent));
    this.group.add(arch);
  }

  /** The finish line: a giant golden Chinese coin (round, with a square hole the
   * track passes through). Decorative — never blocks the finish. */
  private finishCoinGate(t: number): void {
    const fr = this.spline.frameAt(t);
    const hw = fr.halfWidth;
    const s = hw + 1.2; // square hole half-size — spans the track
    const rOuter = s * 1.5; // golden disc outer radius

    const shape = new THREE.Shape();
    shape.absarc(0, 0, rOuter, 0, Math.PI * 2, false);
    const hole = new THREE.Path();
    hole.moveTo(-s, -s);
    hole.lineTo(s, -s);
    hole.lineTo(s, s);
    hole.lineTo(-s, s);
    hole.closePath();
    shape.holes.push(hole);

    const geo = new THREE.ExtrudeGeometry(shape, {
      depth: 1.0,
      bevelEnabled: true,
      bevelThickness: 0.3,
      bevelSize: 0.3,
      bevelSegments: 2,
      steps: 1,
      curveSegments: 56,
    });
    geo.center();
    const mat = new THREE.MeshStandardMaterial({
      color: 0xffcf3a,
      metalness: 0.85,
      roughness: 0.28,
      emissive: 0x4a3500,
    });
    const coin = new THREE.Mesh(geo, mat);
    coin.castShadow = true;
    // Orient the coin face across the track (local X=right, Y=up, Z=tangent).
    coin.quaternion.setFromRotationMatrix(new THREE.Matrix4().makeBasis(fr.right, fr.up, fr.tangent));
    // Raise it so the square hole spans the track (its bottom edge under ground).
    coin.position.copy(fr.pos.clone().addScaledVector(fr.up, s - 1.2));
    this.group.add(coin);
  }

  private buildStartFinish(): void {
    this.gate(this.toSplineT(0.002), 0x55c2ff, 'start');
    this.finishCoinGate(this.finishT);
  }

  /** Animate collectibles (spin + bob) and toppling bamboo. */
  update(dt: number, time: number): void {
    this.animateBamboo(dt);
    for (const item of this.collectibles) {
      if (item.collected) continue;
      const mesh = this.collectMeshes.get(item.tier)!;
      const bob = Math.sin(time * 2 + item.phase) * 0.18;
      item.pos.copy(item.basePos).addScaledVector(new THREE.Vector3(0, 1, 0), bob);
      this.dummy.position.copy(item.pos);
      // Coins stand upright (disc facing sideways) and flash as they spin.
      this.dummy.rotation.set(item.tier === 'coin' ? Math.PI / 2 : 0, time * 1.6 + item.phase, 0);
      this.dummy.scale.setScalar(1);
      this.dummy.updateMatrix();
      mesh.setMatrixAt(item.meshIndex, this.dummy.matrix);
    }
    for (const mesh of this.collectMeshes.values()) mesh.instanceMatrix.needsUpdate = true;
  }

  dispose(): void {
    this.group.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.geometry) m.geometry.dispose();
    });
  }
}
